import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

interface UseInfiniteScrollOptions {
  /** Whether there are more items to load */
  hasNextPage: boolean;
  /** Whether a load is currently in progress */
  isLoading: boolean;
  /** Callback to load the next page */
  onLoadMore: () => void | Promise<void>;
  /** IntersectionObserver rootMargin for triggering early (default: '200px') */
  rootMargin?: string;
  /** IntersectionObserver threshold (default: 0) */
  threshold?: number;
  /**
   * Optional ref to the scroll container element used as the IntersectionObserver root.
   * Defaults to the browser viewport when omitted. Pass the scroll container ref when
   * the sentinel lives inside a clipped overflow element (e.g. a modal scroll area).
   */
  root?: RefObject<Element | null>;
}

export function useInfiniteScroll({
  hasNextPage,
  isLoading,
  onLoadMore,
  rootMargin = '200px',
  threshold = 0,
  root,
}: UseInfiniteScrollOptions) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const handleIntersect = useCallback(
    async (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (!entry.isIntersecting || isLoading || isLoadingMore || !hasNextPage) return;

      setIsLoadingMore(true);
      try {
        await onLoadMore();
      } finally {
        setIsLoadingMore(false);
      }
    },
    [hasNextPage, isLoading, isLoadingMore, onLoadMore],
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(handleIntersect, {
      root: root?.current ?? null,
      rootMargin,
      threshold,
    });

    observerRef.current.observe(sentinel);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [handleIntersect, root, rootMargin, threshold]);

  return { sentinelRef, isLoadingMore };
}
